import { useEffect, useState } from "react";
import { WarningOctagon, Plus, PencilSimple, Trash, Buildings, Wrench } from "@phosphor-icons/react";
import { Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageHero } from "@/components/PageHero";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { TIPI_INCIDENTE, GRAVITA_INCIDENTE } from "@/lib/constants";
import { toast } from "sonner";

const emptyForm = () => ({
  tenant_id: "", data: "", tipo: "near-miss", luogo: "", descrizione: "",
  gravita: "lieve", persone_coinvolte: "", giorni_assenza: "",
});

const tipoBadge = (t) => {
  const map = {
    "near-miss": "bg-blue-100 text-blue-800 border-blue-200",
    "incidente": "bg-amber-100 text-amber-800 border-amber-200",
    "infortunio": "bg-rose-100 text-rose-700 border-rose-200",
  };
  return <Badge className={`${map[t] || ""} capitalize`}>{t}</Badge>;
};
const gravitaBadge = (g) => {
  const map = {
    lieve: "bg-emerald-100 text-emerald-700 border-emerald-200",
    media: "bg-amber-100 text-amber-800 border-amber-200",
    grave: "bg-rose-100 text-rose-700 border-rose-200",
  };
  return <Badge variant="outline" className={`${map[g] || ""} capitalize`}>{g}</Badge>;
};

export default function Incidents() {
  const { tenantId, organizations, currentOrg } = useTenant();
  const { user } = useAuth();
  const isConsultant = user?.role === "consultant";
  const canWrite = user?.role === "consultant" || user?.role === "client_user";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);

  const load = () => {
    setLoading(true);
    const p = tenantId ? `?tenant_id=${tenantId}` : "";
    api.get(`/incidents${p}`).then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, [tenantId]);

  const openNew = () => { setEditingId(null); setForm({ ...emptyForm(), tenant_id: tenantId || "" }); setOpen(true); };
  const openEdit = (x) => { setEditingId(x.id); setForm({ ...emptyForm(), ...x, giorni_assenza: x.giorni_assenza ?? "" }); setOpen(true); };

  const save = async () => {
    if (!form.tenant_id) { toast.error("Seleziona un cliente"); return; }
    if (!form.descrizione.trim()) { toast.error("Inserisci la descrizione"); return; }
    const payload = { ...form, giorni_assenza: form.giorni_assenza === "" ? null : Number(form.giorni_assenza) };
    delete payload.id; delete payload.created_at; delete payload.has_action;
    try {
      if (editingId) { await api.put(`/incidents/${editingId}`, payload); toast.success("Registrazione aggiornata"); }
      else { await api.post("/incidents", payload); toast.success("Registrazione creata"); }
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Eliminare questa registrazione?")) return;
    try { await api.delete(`/incidents/${id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const createAction = async (id) => {
    try { await api.post(`/incidents/${id}/corrective-action`); toast.success("Azione correttiva creata (vedi Audit → Azioni)"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const orgName = (tid) => organizations.find((o) => o.id === tid)?.nome || "";

  return (
    <div className="space-y-6">
      <PageHero label="UNI EN ISO 45001:2023" title="Registro incidenti e near-miss"
        subtitle={tenantId ? currentOrg?.nome : "Near-miss, incidenti e infortuni con collegamento alle azioni correttive"}>
        {canWrite && (
          <Button onClick={openNew} data-testid="new-incident-btn"
            className="bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white">
            <Plus className="h-4 w-4 mr-2" /> Nuova registrazione
          </Button>
        )}
      </PageHero>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center border-slate-200">
          <WarningOctagon size={40} className="text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Nessuna registrazione{tenantId ? "" : ". Seleziona un cliente per aggiungerne"}.</p>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="incidents-list">
          {items.map((x) => (
            <Card key={x.id} data-testid={`incident-${x.id}`} className="p-4 border-slate-200">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    {tipoBadge(x.tipo)}
                    {gravitaBadge(x.gravita)}
                    <span className="text-xs text-slate-500">{x.data || "—"}{x.luogo ? ` · ${x.luogo}` : ""}</span>
                    {!tenantId && isConsultant && <span className="text-xs text-slate-500 inline-flex items-center gap-1"><Buildings size={13} /> {orgName(x.tenant_id)}</span>}
                    {x.has_action && <Badge className="bg-orange-100 text-orange-700 border-orange-200">Azione correttiva</Badge>}
                  </div>
                  <p className="text-sm text-slate-700 mt-2">{x.descrizione}</p>
                  <div className="flex flex-wrap gap-x-3 mt-1 text-xs text-slate-500">
                    {x.persone_coinvolte && <span>Coinvolti: {x.persone_coinvolte}</span>}
                    {x.giorni_assenza != null && <span>Giorni assenza: {x.giorni_assenza}</span>}
                  </div>
                </div>
                {canWrite && (
                  <div className="flex items-center gap-1 shrink-0">
                    {!x.has_action && (
                      <Button size="sm" variant="outline" className="border-orange-300 text-orange-700 hover:bg-orange-50" data-testid={`incident-action-${x.id}`} onClick={() => createAction(x.id)}>
                        <Wrench size={14} className="mr-1.5" /> Azione correttiva
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" className="h-8 w-8" data-testid={`edit-incident-${x.id}`} onClick={() => openEdit(x)}><PencilSimple size={16} /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" data-testid={`delete-incident-${x.id}`} onClick={() => remove(x.id)}><Trash size={16} /></Button>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editingId ? "Modifica registrazione" : "Nuova registrazione"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {!editingId && (
              <div>
                <Label>Cliente</Label>
                <Select value={form.tenant_id} onValueChange={(v) => setForm({ ...form, tenant_id: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="incident-tenant"><SelectValue placeholder="Seleziona cliente" /></SelectTrigger>
                  <SelectContent>{organizations.map((o) => <SelectItem key={o.id} value={o.id}>{o.nome}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Tipo</Label>
                <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="incident-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>{TIPI_INCIDENTE.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Gravità</Label>
                <Select value={form.gravita} onValueChange={(v) => setForm({ ...form, gravita: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="incident-gravita"><SelectValue /></SelectTrigger>
                  <SelectContent>{GRAVITA_INCIDENTE.map((g) => <SelectItem key={g} value={g} className="capitalize">{g}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Data</Label><Input type="date" value={form.data || ""} data-testid="incident-data" onChange={(e) => setForm({ ...form, data: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div><Label>Luogo</Label><Input value={form.luogo} onChange={(e) => setForm({ ...form, luogo: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Descrizione</Label><Textarea value={form.descrizione} data-testid="incident-descrizione" onChange={(e) => setForm({ ...form, descrizione: e.target.value })} className="mt-1.5" rows={3} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Persone coinvolte</Label><Input value={form.persone_coinvolte} onChange={(e) => setForm({ ...form, persone_coinvolte: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Giorni assenza</Label><Input type="number" value={form.giorni_assenza} onChange={(e) => setForm({ ...form, giorni_assenza: e.target.value })} className="mt-1.5" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="save-incident-btn" className="bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
